import { MouseEventHandler } from 'react';
import styled from 'styled-components';
import {
  CardContent as MuiCardContent,
  ListItem,
  ListItemButton as MuiListItemButton,
  ListItemText as MuiListItemText,
  List,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { MinimalCard } from 'components/styled';
import useIntegrationOptions, {
  IntegrationOptions,
} from 'IntegrationModule/components/IntegrationOptions/useIntegrationOptions';
import colors from 'theme/colors';
import cn from 'classnames';

import { SpacingProps } from '@mui/system';

import { IntegrationHealth, IntegrationState } from 'types';

import { integrationsHash } from '@constants';

import { LinearBuffer } from 'components/Progress';
import { renderIntegrationHealthByRequestAndSuccessCountPercent } from 'utils';
import useAppNavigation from 'hooks/useAppNavigation';
import { useIntegrationsListQuery } from 'IntegrationModule/api/queries/useIntegrationsListQuery';
import { IntegrationStateIndicator } from 'IntegrationModule/components/IntegrationStateIndicator/IntegrationStateIndicator';
import { getEffectiveState, isIntegrationArchived } from 'IntegrationModule/utils/utils';

const CardContent = styled(MuiCardContent)<SpacingProps>``;

const ListItemText = styled(MuiListItemText)<SpacingProps>``;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type IntegrationListPropsType = {
  onClickProvisionButton: MouseEventHandler<HTMLElement>;
  handleIntegrationNameClick: (targetServiceId: number, targetServiceName: string) => void;
  integrationHealthData: {
    [targetService: string]: {
      [targetServiceId: number]: IntegrationHealth;
    };
  };
  teamId: number;
  /** When true, the card will stretch to fill available flex space */
  flexGrow?: boolean;
  /** Optional tenant for API calls - overrides global state */
  tenant?: string;
  /** Optional realmId for API calls - overrides global state */
  realmId?: string;
};

/**
 * Minimum height to show ~3 integration rows.
 * Calculation: (3 rows × 60px) + header (80px) = 260px
 */
const MIN_HEIGHT_FOR_3_ROWS = 260;

const IntegrationList = ({
  handleIntegrationNameClick,
  onClickProvisionButton,
  integrationHealthData,
  teamId,
  flexGrow = false,
  tenant,
  realmId,
}: IntegrationListPropsType) => {
  const { navigateToIntegrationHealth } = useAppNavigation();

  const { integrationsList, isLoadingIntegrationsList } = useIntegrationsListQuery({
    mosaicTeamId: teamId,
    includeArchived: true,
    includeDrafts: true,
    tenant,
    realmId,
  });
  const { IntegrationOptionsMenu, ...integrationOptionsProps } = useIntegrationOptions();

  return (
    <MinimalCard
      $hoverEffect={false}
      sx={{
        mb: 6,
        minHeight: MIN_HEIGHT_FOR_3_ROWS,
        ...(flexGrow && {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }),
      }}
    >
      <CardContent sx={{ pb: 0 }}>
        <HeaderContainer>
          <Typography variant="h6" gutterBottom>
            Integration List
          </Typography>
          <Typography variant="body2" gutterBottom>
            List of Integration ({integrationsList && integrationsList.length})
          </Typography>
        </HeaderContainer>

        <Button color="primary" variant="outlined" onClick={onClickProvisionButton}>
          Provision
        </Button>
      </CardContent>

      {isLoadingIntegrationsList ? (
        <LinearBuffer />
      ) : (
        <List
          style={{
            paddingBottom: 0,
            overflow: 'auto',
            ...(flexGrow && { flex: 1 }),
          }}
        >
          {integrationsList && integrationsList.length > 0 ? (
            integrationsList.map((integration) => {
              const { targetService, targetServiceId, settings, id } = integration;
              const effectiveState = getEffectiveState(integration);
              const archived = isIntegrationArchived(integration);
              const integrationHealth = integrationHealthData[targetService]?.[targetServiceId];
              const ListItemPrimary = (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <IntegrationStateIndicator
                    state={effectiveState}
                    deletionInProgress={integration.deletionInProgress}
                  />
                  {integrationsHash[targetService]?.label}
                  {settings?.label ? ` - ${settings.label}` : ''}
                </Box>
              );

              return (
                <ListItem key={id} disablePadding>
                  <StyledListItemButton
                    className={cn({ archived })}
                    onClick={() => {
                      if (archived) return;
                      handleIntegrationNameClick(targetServiceId, targetService);
                    }}
                  >
                    <ListItemText
                      primary={ListItemPrimary}
                      secondary={`Integration Service ID: ${targetServiceId}`}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      {(effectiveState === IntegrationState.ACTIVE ||
                        effectiveState === IntegrationState.SUSPENDED) &&
                        renderIntegrationHealthByRequestAndSuccessCountPercent({
                          integrationHealth,
                          className: 'icon-only',
                          onClick: (e) => {
                            e.stopPropagation();
                            navigateToIntegrationHealth({
                              targetService,
                              targetServiceId,
                              teamId,
                            });
                          },
                        })}
                      <IntegrationOptions
                        targetServiceId={targetServiceId}
                        teamId={teamId}
                        state={effectiveState}
                        targetService={targetService}
                        customDailyFetchCount={settings?.customDailyFetchCount}
                        customDailyFetchTime={settings?.customDailyFetchTime}
                        showSettingButton={false}
                        tenant={tenant}
                        realmId={realmId}
                        teamIntegrations={integrationsList}
                        {...integrationOptionsProps}
                      />
                    </Box>
                  </StyledListItemButton>
                </ListItem>
              );
            })
          ) : (
            <ListItem>
              <ListItemText primary="Your team does not have any integration at this time" />
            </ListItem>
          )}
        </List>
      )}
      {IntegrationOptionsMenu}
    </MinimalCard>
  );
};

export default IntegrationList;

const StyledListItemButton = styled(MuiListItemButton)`
  background-color: transparent;

  &:hover {
    background-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)'};
  }

  &.archived {
    background-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.1)' : colors.gainsboro};
    color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.6)' : colors.lightGrey};

    &:hover {
      cursor: default;
      background-color: ${({ theme }) =>
        theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.1)' : colors.gainsboro};
    }
  }
`;
